import cn from 'classnames';
import { ReactNode } from 'react';
import IncrementalityIcon from '@src/assets/IncrementalityIcon.svg?react';

interface SnapshotChartLegendProps {
  name: string;
  value: number;
  value2: number | undefined;
  computedValue?: (value: number, value2?: number) => string;
  formatter: (value: number) => string;
  incrementality?: boolean;
  expanded?: boolean;
  label?: number | string;
  label2?: number | string;
  actualValues?: boolean;
  onClick?: () => void;
  icon?: ReactNode;
}

export const SnapshotChartLegend = ({
  name,
  value,
  value2,
  computedValue,
  label2,
  label,
  formatter,
  incrementality,
  expanded,
  actualValues = false,
  onClick,
  icon
}: SnapshotChartLegendProps) => {
  // Split off the last word so it can share a no-wrap group with the incrementality icon.
  // This lets the label wrap on long names while preventing the icon from orphaning
  // onto its own line — CSS alone can't target the last word of a text node.
  const lastSpaceIdx = name.lastIndexOf(' ');
  const leadingText = lastSpaceIdx === -1 ? '' : name.slice(0, lastSpaceIdx + 1);
  const lastWord = lastSpaceIdx === -1 ? name : name.slice(lastSpaceIdx + 1);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={cn('snapshot-chart-legend', { 'has-computed-value': Boolean(computedValue) })}
      onClick={onClick}
    >
      <div className='item-label'>
        {icon && <span className='item-label-icon'>{icon}</span>}
        <div>
          {leadingText}
          <span className='last-word-group'>
            {lastWord}
            {incrementality && <IncrementalityIcon className='incrementality-icon' />}
          </span>
        </div>
      </div>
      {computedValue && (
        <div className='item-computed-value'>
          <div>{computedValue(value, value2)}</div>
        </div>
      )}
      {expanded ? (
        <div className='item-value-wrapper'>
          <div className='item-value'>
            <div className='item-name-and-percent'>
              <span>Spend:</span>
              <span className='snapshot-chart-legend-percent-span'>{formatter(value)}</span>
            </div>
            {actualValues && <span className='actual-values-span'>{label}</span>}
          </div>
          {value2 !== undefined && (
            <div className='item-value'>
              <div className='item-name-and-percent'>
                <span>Effect:</span>
                <span className='snapshot-chart-legend-percent-span'>{formatter(value2)}</span>
              </div>
              {actualValues && <span className='actual-values-span'>{label2}</span>}
            </div>
          )}
        </div>
      ) : (
        <div className='item-value-wrapper'>
          <div className='item-value'>{typeof label === 'string' ? label : formatter(value)}</div>
          {value2 !== undefined && (
            <div className='item-value'>
              {typeof label2 === 'string' ? label2 : formatter(value2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
